import { useRef, useState } from "react";
import { blockingIssues, evaluation, type ValidationAction, type ValidationEvaluation, type ValidationIssue } from "@shared/validation/issues";
import { fieldFindings } from "@web/ui/ValidationSummary";
import { ApiError } from "./api";

/** Command parsing and merged-record checks can report the same field error.
 * Collapse only identical presentation; distinct records/pairs remain distinct. */
export function formFindings(issues: readonly ValidationIssue[]): ValidationIssue[] {
  const found = new Map<string, ValidationIssue>();
  for (const issue of issues) {
    const key = JSON.stringify([issue.entity, issue.kind, [...issue.fields].sort(), issue.message, issue.pair, issue.resource, issue.related]);
    const previous = found.get(key);
    found.set(key, previous ? { ...previous, blocks: [...new Set([...previous.blocks, ...issue.blocks])] } : issue);
  }
  return [...found.values()];
}
export const validationUnavailable = (error: unknown) => !(error instanceof ApiError) || error.status >= 500 || error.checksComplete === false;

/** Transport/state only. Callers supply the shared domain/schema findings. */
export function useFormValidation(local: ValidationEvaluation) {
  const locked = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ValidationIssue[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const result = evaluation(formFindings([...local.issues, ...failure]), false);
  const clear = () => { setFailure([]); setUnavailable(false); };
  return {
    result, busy, unavailable,
    edit(change: () => void) { if (!locked.current) { clear(); change(); } },
    begin(action: ValidationAction) {
      // A failed server check is displayed until edit/retry, not a permanent
      // client-side veto: related state may have been repaired meanwhile.
      if (locked.current || blockingIssues(local, action).length) return false;
      locked.current = true; setBusy(true); return true;
    },
    finish() { locked.current = false; setBusy(false); },
    reject(error: unknown) {
      setFailure(error instanceof ApiError ? error.issues ?? [] : []);
      setUnavailable(validationUnavailable(error));
    },
    report(issues: ValidationIssue[]) { setFailure(issues); },
    clear,
    field(name: string) {
      const feedback = fieldFindings(result.issues, name);
      return { "data-validation-field": name, disabled: busy,
        ...(feedback.error ? { error: feedback.error } : {}), ...(feedback.hint ? { hint: feedback.hint } : {}) };
    },
  };
}

/** Preserve untouched legacy fields rather than resubmitting a normalized view.
 * Form snapshots are plain values; no schema parsing/defaulting occurs here. */
export function changedValues<T extends Record<string, unknown>>(current: T, before: T): Partial<T> {
  return Object.fromEntries(Object.entries(current).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(before[key]))) as Partial<T>;
}
